import Pusher, { Channel } from "pusher-js";
import {
  BenchmarkTraceEvent,
  Message,
  MessageRole,
  InterviewerPusherEventData,
  RequestStates,
} from "../types";
import { generateUUID } from "../utils";

export type OnMessageCallback = (message: Message) => void;
export type OnStatusCallback = (status: {
  phase?: string;
  status?: string;
}) => void;
export type OnErrorCallback = (error: string) => void;
export type OnBenchmarkTraceCallback = (event: BenchmarkTraceEvent) => void;

export class PusherService {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private channelName: string;

  constructor(
    private key: string,
    private cluster: string,
    channelName: string,
    private onMessage: OnMessageCallback,
    private onStatus: OnStatusCallback,
    private onError: OnErrorCallback,
    private onBenchmarkTrace?: OnBenchmarkTraceCallback,
  ) {
    this.channelName = channelName;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.pusher) {
        console.warn("Pusher already connected");
        resolve();
        return;
      }

      console.log(
        "Connecting to Pusher with key:",
        this.key,
        "cluster:",
        this.cluster,
      );

      this.pusher = new Pusher(this.key, {
        cluster: this.cluster,
      });

      this.channel = this.pusher.subscribe(this.channelName);

      // Wait for subscription to succeed before resolving
      this.channel.bind("pusher:subscription_succeeded", () => {
        console.log("Successfully subscribed to channel:", this.channelName);
        resolve();
      });

      this.channel.bind("pusher:subscription_error", (error: unknown) => {
        console.error("Subscription error:", error);
        this.onError("Failed to connect to real-time channel");
        reject(new Error("Failed to subscribe to Pusher channel"));
      });

      const buildAssistantMessage = (
        data: InterviewerPusherEventData,
      ): Message => ({
        id: data.messageId || generateUUID(),
        role: MessageRole.ASSISTANT,
        content: data.content ?? "",
        timestamp: new Date(),
        ...(data.benchmarkTurnId
          ? { benchmarkTurnId: data.benchmarkTurnId }
          : {}),
        inputType: data.inputType,
        options: data.options,
        language: data.language,
        questionId: data.interviewQuestionId,
        questionType: data.questionType,
        status: RequestStates.Success,
        uploadUrl: data.uploadUrl,
        audioUrl: data.audioUrl,
        responseFormat: data.responseFormat,
        canSkip: data.canSkip,
        recordingDurationSeconds: data.recordingDurationSeconds,
        // AS-1564 — pass progress fields through. The synthetic
        // `interviewer.completed` event ships these as undefined; the
        // useInterview omit-vs-preserve contract keeps the bar at the
        // 100% value set by the preceding CLOSING `response` event.
        // Guard with `Number.isFinite` so a future explicit-null /
        // explicit-NaN Pusher payload (e.g. JSON null roundtrip) doesn't
        // pass the hook's `!== undefined` guard and clobber valid state.
        currentQuestionNumber: Number.isFinite(data.currentQuestionNumber)
          ? data.currentQuestionNumber
          : undefined,
        totalQuestions: Number.isFinite(data.totalQuestions)
          ? data.totalQuestions
          : undefined,
      });

      // Listen for interview started event
      this.channel.bind(
        "interviewer.started",
        (data: InterviewerPusherEventData) => {
          if (data.content) {
            this.onMessage(buildAssistantMessage(data));
          }
          if (data.phase || data.status) {
            this.onStatus({ phase: data.phase, status: data.status });
          }
        },
      );

      // Listen for AI response event
      this.channel.bind(
        "interviewer.response",
        (data: InterviewerPusherEventData) => {
          console.log(
            "[Pusher] Received interviewer.response on channel:",
            this.channelName,
            data,
          );
          if (data.content) {
            this.onMessage(buildAssistantMessage(data));
          }
          if (data.phase || data.status) {
            this.onStatus({ phase: data.phase, status: data.status });
          }
        },
      );

      // Listen for interview completed event
      this.channel.bind(
        "interviewer.completed",
        (data: InterviewerPusherEventData) => {
          console.log("Received interviewer.completed:", data);
          if (data.content) {
            const message: Message = {
              id: generateUUID(),
              role: MessageRole.ASSISTANT,
              content: data.content,
              timestamp: new Date(),
              status: RequestStates.Success,
            };
            this.onMessage(message);
          }
          this.onStatus({ status: "completed" });
        },
      );

      // Listen for timeout event
      this.channel.bind(
        "interviewer.timeout",
        (data: InterviewerPusherEventData) => {
          console.log("Received interviewer.timeout:", data);
          this.onStatus({ status: "timeout" });
          this.onError("Interview session timed out");
        },
      );

      // Listen for error event
      this.channel.bind(
        "interviewer.error",
        (data: InterviewerPusherEventData) => {
          console.log("Received interviewer.error:", data);
          if (data.error) {
            this.onError(data.error);
          }
        },
      );

      if (this.onBenchmarkTrace) {
        this.channel.bind("interviewer.benchmark_trace", this.onBenchmarkTrace);
      }
    });
  }

  disconnect(): void {
    if (this.channel) {
      this.channel.unbind_all();
      this.pusher?.unsubscribe(this.channelName);
      this.channel = null;
    }

    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }
  }
}
